import { resupplyConfigFor, resupplyLendersByChain } from '@1delta/data-sdk'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'
import { resupplyLenderKey } from '../../../lending/public-data/resupply/convertPublic'
import { getResupplyPairRoster } from './resupplyRoster'

/**
 * Resupply reUSD price fetcher.
 *
 * WHY THIS IS NEEDED AT ALL. reUSD is priced by NOTHING else in the pipeline,
 * and the ticker is a three-way collision — `Re Protocol reUSD::REUSD` and
 * `Realtoken Ecosystem USD::REUSD` both carry prices, and both are unrelated
 * tokens. So the qualified group `Resupply USD::REUSD` resolved to no price at
 * all: every USD figure on the debt leg came out `$0`, which made a loop quote
 * read as +100 % impact. Per this directory's own rule, a lender is not
 * integrated until its markets have accurate oracle prices; Resupply was the
 * gap.
 *
 * THE FEED IS THE PROTOCOL'S OWN. `PriceWatcher.oracle()` (held in config as
 * `reusdOracle`) exposes `price()` as an 18-decimal USD price — the number
 * Resupply itself watches to drive the off-peg amplifier in its borrow rate.
 * It read 0.98983 on 2026-08-08, which agrees with what the loop's own Curve
 * route actually pays out to four decimals (989.76 crvUSD per 1,000 reUSD).
 *
 * **Do NOT peg reUSD to $1 here.** It is tempting — the pair's solvency math
 * treats both legs as a dollar — but reUSD genuinely trades below par, and the
 * gap is the single largest cost of entering a leveraged position (100k of
 * face debt buys ~99.2k of collateral). Pegging it would hide exactly the
 * number a user needs, and would disagree with the swap the same transaction
 * performs. The par assumption belongs to the pair's LTV check, not to a price
 * feed.
 *
 * Direct USD (PASS 1) → feeds the shared `usdPrices` map, which is what
 * `selectAssetGroupPrices` flattens into the group-keyed prices the action API
 * reads.
 *
 * The collateral leg needs nothing here: every pair's underlying is crvUSD or
 * frxUSD, both already priced by the shared pipeline (verified against the
 * live map 2026-08-08 — crvUSD resolves under the bare `CRVUSD` group).
 */

interface ResupplyOracleMeta {
  lender: string
  chainId: string
  /** The pair this entry is keyed to. */
  pair: string
  /** reUSD, lowercased. */
  reusd: string
}

/**
 * One `price()` call per PAIR.
 *
 * The feed is global — one reUSD price for the whole protocol — so every call
 * is identical. They are issued per pair anyway because entries have to be
 * keyed by the same per-market `RESUPPLY_<chainId>_<PAIR>` lender key the
 * lending converter emits, or the `marketUid` join does not line up. The
 * multicall cost is trivial (21 identical view calls in one batch) and the
 * alternative — one entry under a bare lender key — would price the group but
 * leave every market's `oraclePrice` blank.
 */
export function getResupplyCalls(
  chainId: string,
): FetcherResult<ResupplyOracleMeta>[] {
  const results: FetcherResult<ResupplyOracleMeta>[] = []

  for (const lender of resupplyLendersByChain(chainId)) {
    const cfg = resupplyConfigFor(lender, chainId)
    if (!cfg?.reusdOracle || !cfg?.reusd) continue

    // Resolved by the public-data pass; deliberately NOT re-discovered here.
    const roster = getResupplyPairRoster(chainId, lender)
    if (roster.length === 0) continue

    const reusd = cfg.reusd.toLowerCase()
    for (const entry of roster) {
      const calls: MulticallCall[] = [
        { address: cfg.reusdOracle, name: 'price', params: [] },
      ]
      results.push({
        calls,
        meta: { lender, chainId, pair: entry.pair.toLowerCase(), reusd },
        lender: resupplyLenderKey(lender, chainId, entry.pair),
      })
    }
  }

  return results
}

/** Parse the oracle's 18-decimal USD price into a direct-USD reUSD entry. */
export function parseResupplyResults(
  data: any[],
  meta: ResupplyOracleMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId } = context
  const key = resupplyLenderKey(meta.lender, chainId, meta.pair)

  const raw = data?.[0]
  const price =
    typeof raw === 'bigint'
      ? raw
      : raw != null && raw !== '0x'
        ? BigInt(raw)
        : 0n
  if (price === 0n) return []

  const usd = Number(price) / 1e18
  // A stablecoin feed that has left this band is a broken read, not news:
  // reUSD has traded 0.98–1.00 since launch and the protocol's own rate model
  // would be in emergency territory outside it. Refuse rather than pollute the
  // shared map — the same guard the Liquity/River/Inverse fetchers carry.
  if (!(usd > 0.5 && usd < 2)) return []

  return [
    {
      asset: meta.reusd,
      price: usd,
      priceUSD: usd,
      marketUid: createMarketUid(chainId, key, meta.reusd),
      targetLender: key,
      description: 'Resupply reUSD oracle (PriceWatcher feed, USD)',
    },
  ]
}

/** `price()` on the reUSD oracle — 18-decimal USD. */
export function getResupplyAbi() {
  return [
    {
      name: 'price',
      type: 'function',
      stateMutability: 'view',
      inputs: [],
      outputs: [{ type: 'uint256' }],
    },
  ] as const
}

/** Resupply reUSD Oracle Fetcher module. */
export const resupplyFetcher = {
  getCalls: getResupplyCalls,
  parse: parseResupplyResults,
  getAbi: getResupplyAbi,
}
